import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Textarea from "@mui/joy/Textarea";
import Typography from "@mui/joy/Typography";
import { NotificationService } from "../../../common/notification";
import { AdminExternalProviderInfo } from "../../../models/externalMarkets";
import { extProviderSettingsApi, extSystemUserNameApi } from "../externalMarketsApi";
import { ConnectionSettings } from "./ConnectionSettings";

type Props = {
    provider: AdminExternalProviderInfo;
    onChanged: () => void;
};

/** Ограничение сервера на длину ника */
const MAX_NAME_LENGTH = 32;

/** SteamId64 — 17 цифр */
const STEAM_ID = /^\d{17}$/;

export function parseSteamIds(text: string): { ids: string[]; invalid: string[] } {
    const tokens = text
        .split(/[\s,;]+/)
        .map((t) => t.trim())
        .filter(Boolean);
    const ids = Array.from(new Set(tokens.filter((t) => STEAM_ID.test(t))));
    const invalid = tokens.filter((t) => !STEAM_ID.test(t));
    return { ids, invalid };
}

export function SettingsTab(props: Props) {
    const { provider, onChanged } = props;
    const [botsText, setBotsText] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [name, setName] = React.useState("");
    const [savingName, setSavingName] = React.useState(false);

    React.useEffect(() => {
        setBotsText((provider.partnerBotSteamIds ?? []).map(String).join("\n"));
        setName(provider.systemUserName ?? "");
    }, [provider]);

    const { ids, invalid } = parseSteamIds(botsText);
    const trimmedName = name.trim();
    const nameError = !trimmedName
        ? "Ник не может быть пустым"
        : trimmedName.length > MAX_NAME_LENGTH
            ? `Не длиннее ${MAX_NAME_LENGTH} символов`
            : undefined;

    return (
        <Stack spacing={2}>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm", maxWidth: 720 }}>
                <Stack spacing={2}>
                    <FormControl error={!!nameError}>
                        <FormLabel>Ник продавца</FormLabel>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                        <FormHelperText>
                            {nameError ??
                                `Под этим ником покупатели видят предметы поставщика. Системный пользователь #${provider.systemUserId}. Ник должен быть свободен.`}
                        </FormHelperText>
                    </FormControl>
                    <Box>
                        <Button
                            loading={savingName}
                            disabled={!!nameError || trimmedName === (provider.systemUserName ?? "")}
                            onClick={async () => {
                                setSavingName(true);
                                try {
                                    const res = await extSystemUserNameApi(provider.key, trimmedName);
                                    if (res.result) {
                                        NotificationService.alertSuccess("Ник продавца сохранён");
                                        onChanged();
                                    } else {
                                        NotificationService.alertError(res.error ?? "Не удалось сохранить ник");
                                    }
                                } finally {
                                    setSavingName(false);
                                }
                            }}
                        >
                            Сохранить ник
                        </Button>
                    </Box>
                </Stack>
            </Sheet>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm", maxWidth: 720 }}>
                <Stack spacing={2}>
                    <FormControl error={invalid.length > 0}>
                        <FormLabel>SteamId ботов поставщика</FormLabel>
                        <Textarea
                            minRows={4}
                            value={botsText}
                            placeholder="76561198000000000"
                            onChange={(e) => setBotsText(e.target.value)}
                        />
                        <FormHelperText>
                            {invalid.length > 0
                                ? `Не SteamId64: ${invalid.join(", ")}`
                                : "По одному в строке. Steam-аккаунты поставщика, с которых приходят трейды: трейд от них с совпадающим составом бот принимает сразу. Если пусто, бот ждёт, пока фоновая проверка узнает номер офера у поставщика (задержка до цикла опроса)."}
                        </FormHelperText>
                    </FormControl>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            loading={saving}
                            disabled={invalid.length > 0}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    const res = await extProviderSettingsApi(provider.key, ids);
                                    if (res.result) {
                                        NotificationService.alertSuccess("Настройки поставщика сохранены");
                                        onChanged();
                                    } else {
                                        NotificationService.alertError(res.error ?? "Не удалось сохранить настройки");
                                    }
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            Сохранить
                        </Button>
                        <Typography level="body-xs">Ботов: {ids.length}</Typography>
                    </Stack>
                </Stack>
            </Sheet>
            <ConnectionSettings provider={provider} onChanged={onChanged} />
        </Stack>
    );
}
